import CreateMarketItchConnectionForm, {
    itchConnectionSchema,
} from '@/routes/_authed/$client_code/markets/create/-components/itchConnectionForm.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCreateClientMarket, useGetAllTemplateMarkets } from '@/gen/publicApi'

import Breadcrumbs from '@/containers/breadcrumbs'

import Content from '@/components/content'
import { Form } from '@/components/form'
import FormBlock from '@/components/formBlock'
import FormBlockSubmitButton from '@/components/formBlock/button'
import PageHeader from '@/components/pageHeader'
import Spinner from '@/components/spinner'
import { useToast } from '@/components/toast/useToast'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import { cn } from '@/utils/cn'
import { toLocalTime } from '@/utils/time'

import CreateMarketBasicInfoForm, { basicInfoSchema } from './-components/basicInfoForm'
import CreateMarketFixConnectionForm, { fixConnectionSchema } from './-components/fixConnectionForm'
import CreateMarketUseTemplateForm, { noTemplateSchema, yesTemplateSchema } from './-components/templateForm'
import CreateMarketTimezoneForm, { timezoneSchema } from './-components/timezoneForm'
import CreateMarketTimingForm, { down, timingSchema, up } from './-components/timingForm'
import CreateMarketTypeForm, { typeSchema } from './-components/typeForm'

export const Route = createFileRoute('/_authed/$client_code/markets/create/')({
    component: CreateMarket,
})

function CreateMarket() {
    const { client_code } = Route.useParams()
    const navigate = useNavigate()
    const { toast } = useToast()
    // We'll have to rethink how to pull in template markets if someone does get
    // over 500. Will probably have to be a component with a search on it
    const { data: templateMarketData, isLoading } = useGetAllTemplateMarkets(client_code, {
        Limit: 500,
    })

    useSetBreadcrumbs([
        {
            to: '/$client_code/markets',
            text: 'Markets',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/markets/create',
            text: 'Create market',
            params: { client_code },
        },
    ])
    const createMarketMutator = useCreateClientMarket(client_code, {
        mutation: {
            onSuccess: data => {
                navigate({
                    to: `/$client_code/markets/$market_code`,
                    params: {
                        client_code,
                        market_code: data.code,
                    },
                })
            },
            onError: error => {
                console.error('Error creating market', error)
                toast({
                    variant: 'destructive',
                    title: 'Market creation failed!',
                    description: 'Market creation failed, please try again',
                })
            },
        },
    })

    const baseSchema = basicInfoSchema
    const noTemplateFormSchema = baseSchema
        .extend(noTemplateSchema.shape)
        .extend(timezoneSchema.shape)
        .extend(fixConnectionSchema.shape)
        .extend(itchConnectionSchema.shape)
        .extend(typeSchema.shape)
        .extend(timingSchema.shape)

    const yesTemplateFormSchema = baseSchema
        .extend(yesTemplateSchema.shape)
        .extend(fixConnectionSchema.shape)
        .extend(itchConnectionSchema.shape)
        .merge(z.object({ type: z.literal('Persistent') })) // Just here for the default

    const formSchema = z.discriminatedUnion('useTemplate', [noTemplateFormSchema, yesTemplateFormSchema])

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onBlur',
        defaultValues: {
            useTemplate: true,
            createFixConnection: true,
            createItchConnection: true,
            type: 'Persistent',
        },
    })

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        createMarketMutator.mutate({
            createFixConnection: values.createFixConnection,
            createItchConnection: values.createItchConnection,
            name: values.name,
            marketCode: values.code,
            description: values.description,
            timezone: values.useTemplate ? null : values.timezone,
            marketType: values.type,
            templateMarketCode: values.useTemplate ? values.templateMarketCode : null,
            // TODO - we'll adda more dynamic UI for these values at some point
            priceTickRules: values.useTemplate
                ? null
                : [
                      {
                          startPrice: '0',
                          endPrice: null,
                          tick: '0.001',
                      },
                  ],
            unitTickRules: values.useTemplate
                ? null
                : [
                      {
                          startPrice: '0',
                          endPrice: null,
                          tick: '1',
                      },
                  ],
            timingConfig:
                values.useTemplate || values.type === 'Ephemeral'
                    ? undefined
                    : {
                          name: 'Default',
                          downTime: toLocalTime(up),
                          initTime: toLocalTime(down),
                          timingConfigItems: values.timingConfigs
                              .filter(tc => !!tc)
                              .map(tc => ({
                                  time: toLocalTime(tc.value),
                                  timingEvent: tc.type,
                              })),
                      },
        })
    }

    if (isLoading) {
        return <Spinner size="sm" absolute navVisible />
    }

    if (!templateMarketData?.data) {
        return <p>Hmmm</p>
    }

    const useTemplate = form.watch('useTemplate')
    const marketType = form.watch('type')
    const hiddenOnNoTemplateClassName = cn({ hidden: useTemplate })
    const hiddenOnEphemeralClassName = cn({ hidden: marketType === 'Ephemeral' })

    return (
        <>
            <Breadcrumbs />
            <Content>
                <PageHeader title="Create market" />

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <FormBlock title="Basic information">
                            <CreateMarketBasicInfoForm form={form as any} />
                        </FormBlock>
                        <FormBlock
                            title="Template market"
                            subTitle="Using at template inherits all timing settings, timezone, and instruments."
                        >
                            <CreateMarketUseTemplateForm
                                form={form as any}
                                templateMarketData={templateMarketData.data}
                            />
                        </FormBlock>

                        <FormBlock
                            className={hiddenOnNoTemplateClassName}
                            title="Market type"
                            subTitle="Whether you want the market to be persistent with custom timing events or ephemeral and always open."
                        >
                            <CreateMarketTypeForm form={form as any} />
                        </FormBlock>

                        <FormBlock
                            className={hiddenOnNoTemplateClassName}
                            title="Timezone"
                            subTitle="The timezone the market will be timed around"
                        >
                            <CreateMarketTimezoneForm form={form as any} />
                        </FormBlock>
                        <FormBlock
                            className={cn(hiddenOnNoTemplateClassName, hiddenOnEphemeralClassName)}
                            title="Market timing"
                            subTitle="Events that control the behaviour of the market"
                        >
                            <CreateMarketTimingForm form={form as any} />
                        </FormBlock>
                        <FormBlock title="Connections">
                            <CreateMarketFixConnectionForm form={form as any} className="mb-4" />
                            <CreateMarketItchConnectionForm form={form as any} />
                        </FormBlock>

                        <FormBlockSubmitButton
                            type="submit"
                            disabled={!form.formState.isValid || createMarketMutator.isPending}
                        >
                            {createMarketMutator.isPending ? 'Creating' : 'Create'}
                        </FormBlockSubmitButton>
                    </form>
                </Form>
            </Content>
        </>
    )
}
